import { ServerRegistry } from './servers'
import { SocketManager } from './socket'
import { SortController } from './sort'
import { GraphDisplayManager } from './graph'
import { PercentageBar } from './percbar'
import { FavoritesManager } from './favorites'
import { Tooltip, Caption, formatNumber } from './util'

export class App {
  publicConfig

  constructor () {
    this.tooltip = new Tooltip()
    this.caption = new Caption()
    this.serverRegistry = new ServerRegistry(this)
    this.socketManager = new SocketManager(this)
    this.sortController = new SortController(this)
    this.graphDisplayManager = new GraphDisplayManager(this)
    this.percentageBar = new PercentageBar(this)
    this.favoritesManager = new FavoritesManager(this)

    this._taskIds = []
  }

  // Called once the DOM is ready and the app can begin setup
  init () {
    this.socketManager.createWebSocket()
  }

  setPageReady (isReady) {
    document.getElementById('push').style.display = isReady ? 'block' : 'none'
    document.getElementById('footer').style.display = isReady ? 'block' : 'none'
    document.getElementById('status-overlay').style.display = isReady ? 'none' : 'block'
  }

  setPublicConfig (publicConfig) {
    this.publicConfig = publicConfig

    this.serverRegistry.assignServers(publicConfig.servers)

    // Start repeating frontend tasks once it has received enough data to be considered active
    // This simplifies management logic at the cost of each task needing to safely handle empty data
    this.initTasks()
  }

  handleSyncComplete () {
    this.caption.hide()

    // Load favorites since all servers are registered
    this.favoritesManager.loadLocalStorage()

    // Run a single bulk server sort instead of per-add event since there may be multiple
    this.sortController.show()
    this.percentageBar.redraw()

    // The data may not be there to correctly compute values, but run an attempt
    // Otherwise they will be updated by #initTasks
    this.updateGlobalStats()
  }

  initTasks () {
    this._taskIds.push(setInterval(this.sortController.sortServers, 5000))
    this._taskIds.push(setInterval(this.updatePingTimers, 100))
  }

  handleDisconnect () {
    this.tooltip.hide()

    // Reset individual tracker elements to flush any held data
    this.serverRegistry.reset()
    this.socketManager.reset()
    this.sortController.reset()
    this.graphDisplayManager.reset()
    this.percentageBar.reset()

    // Undefine publicConfig, resynced during the connection handshake
    this.publicConfig = undefined

    // Clear all task ids, if any
    this._taskIds.forEach(clearInterval)

    this._taskIds = []

    // Reset hidden values created by #updateGlobalStats
    this._lastTotalPlayerCount = undefined
    this._lastServerRegistrationCount = undefined

    this._lastMinehutUpdateTime = undefined

    // Reset modified DOM structures
    document.getElementById('stat_totalPlayers').innerText = 0
    document.getElementById('stat_networks').innerText = 0
    document.getElementById('network-totals').innerHTML = ''
    document.getElementById('minehut-leaderboard').innerHTML = ''

    this.setPageReady(false)
  }

  getTotalPlayerCount () {
    return this.serverRegistry.getServerRegistrations()
      .map(serverRegistration => serverRegistration.playerCount)
      .reduce((sum, current) => sum + current, 0)
  }

  getNetworkTotals () {
    const totals = {}
    for (const reg of this.serverRegistry.getServerRegistrations()) {
      const parts = reg.data.name.split(' - ')
      const network = parts[0] || reg.data.name
      if (!totals[network]) {
        totals[network] = { current: 0, peak: 0 }
      }
      totals[network].current += reg.playerCount
      if (reg.lastPeakData) {
        totals[network].peak += reg.lastPeakData.playerCount
      }
    }
    return totals
  }

  updateNetworkTotals () {
    const totals = this.getNetworkTotals()
    const container = document.getElementById('network-totals')

    for (const [network, data] of Object.entries(totals)) {
      const id = `network-total-${network.replace(/\s+/g, '-').toLowerCase()}`
      let el = document.getElementById(id)

      if (!el) {
        el = document.createElement('div')
        el.id = id
        el.className = 'network-total'
        el.innerHTML = `<div class="network-name">${network}</div><div class="network-count">0</div><div class="network-label">players</div><div class="network-peak"></div>`
        container.appendChild(el)
      }

      el.querySelector('.network-count').innerText = formatNumber(data.current)

      const peakEl = el.querySelector('.network-peak')
      if (data.peak > 0) {
        peakEl.innerText = `Peak: ${formatNumber(data.peak)}`
      }
    }
  }

  addServer = (serverId, payload, timestampPoints) => {
    // Even if the backend has never pinged the server, the frontend is promised a placeholder object.
    // result = undefined
    // error = defined with "Waiting" description
    // info = safely defined with configured data
    const serverRegistration = this.serverRegistry.createServerRegistration(serverId)

    serverRegistration.initServerStatus(payload)

    // playerCountHistory is only defined when the backend has previous ping data
    // undefined playerCountHistory means this is a placeholder ping generated by the backend
    if (typeof payload.playerCountHistory !== 'undefined') {
      // Push the historical data into the graph
      // This will trim and format the data so it is ready for the graph to render once init
      serverRegistration.addGraphPoints(payload.playerCountHistory, timestampPoints)

      // Set initial playerCount to the payload's value
      // This will always exist since it is explicitly generated by the backend
      // This is used for any post-add rendering of things like the percentageBar
      serverRegistration.playerCount = payload.playerCount
    }

    // Create the plot instance internally with the restructured and cleaned data
    serverRegistration.buildPlotInstance()

    // Handle the last known state (if any) as an incoming update
    // This triggers the main update pipeline and enables centralized update handling
    serverRegistration.updateServerStatus(payload, this.publicConfig.minecraftVersions)

    // Allow the ServerRegistration to bind any DOM events with app instance context
    serverRegistration.initEventListeners()
  }

  updateMinehutLeaderboard (servers) {
    const container = document.getElementById('minehut-leaderboard')

    if (!servers || servers.length === 0) {
      container.style.display = 'none'
      return
    }

    this._lastMinehutUpdateTime = Date.now()
    container.style.display = ''

    const INITIAL_SHOW = 5

    let html = '<div class="minehut-header"><span class="minehut-title">Minehut Top Servers</span><span class="minehut-timer" id="minehut-timer"></span></div><div class="minehut-servers">'

    servers.forEach((server, i) => {
      const hiddenClass = i >= INITIAL_SHOW ? ' minehut-server-hidden' : ''
      html += `<div class="minehut-server${hiddenClass}" data-minehut-index="${i}">
        <span class="minehut-rank">#${i + 1}</span>
        <span class="minehut-name">${server.name}</span>
        <canvas class="minehut-sparkline" id="minehut-spark_${i}" width="80" height="28"></canvas>
        <span class="minehut-players">${formatNumber(server.playerCount)} players</span>
        <span class="minehut-server-timer" id="minehut-server-timer_${i}"></span>
      </div>`
    })

    html += '</div>'

    if (servers.length > INITIAL_SHOW) {
      html += `<div class="minehut-see-more" id="minehut-see-more">See ${servers.length - INITIAL_SHOW} More Servers</div>`
    }

    container.innerHTML = html

    // Draw sparklines
    servers.forEach((server, i) => {
      if (server.history && server.history.length > 1) {
        this._drawSparkline(`minehut-spark_${i}`, server.history)
      }
    })

    // Bind see-more button
    const seeMoreBtn = document.getElementById('minehut-see-more')
    if (seeMoreBtn) {
      seeMoreBtn.addEventListener('click', () => {
        const hidden = container.querySelectorAll('.minehut-server-hidden')
        hidden.forEach(el => el.classList.remove('minehut-server-hidden'))
        seeMoreBtn.style.display = 'none'
      })
    }
  }

  _drawSparkline (canvasId, history) {
    const canvas = document.getElementById(canvasId)
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const w = canvas.width
    const h = canvas.height
    const points = history

    const min = Math.min(...points)
    const max = Math.max(...points)
    const range = max - min || 1

    ctx.clearRect(0, 0, w, h)

    // Fill area under the line
    ctx.beginPath()
    ctx.moveTo(0, h)
    for (let i = 0; i < points.length; i++) {
      const x = (i / (points.length - 1)) * w
      const y = h - ((points[i] - min) / range) * (h - 4) - 2
      ctx.lineTo(x, y)
    }
    ctx.lineTo(w, h)
    ctx.closePath()
    ctx.fillStyle = 'rgba(108, 92, 231, 0.15)'
    ctx.fill()

    // Draw line
    ctx.beginPath()
    for (let i = 0; i < points.length; i++) {
      const x = (i / (points.length - 1)) * w
      const y = h - ((points[i] - min) / range) * (h - 4) - 2
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.strokeStyle = '#a29bfe'
    ctx.lineWidth = 1.5
    ctx.stroke()

    // Draw endpoint dot
    const lastX = w
    const lastY = h - ((points[points.length - 1] - min) / range) * (h - 4) - 2
    ctx.beginPath()
    ctx.arc(lastX, lastY, 2, 0, Math.PI * 2)
    ctx.fillStyle = '#a29bfe'
    ctx.fill()
  }

  updatePingTimers = () => {
    const now = Date.now()

    for (const reg of this.serverRegistry.getServerRegistrations()) {
      const el = document.getElementById(`ping-timer_${reg.serverId}`)
      if (el) {
        if (reg.lastSuccessfulPingTime) {
          const elapsed = ((now - reg.lastSuccessfulPingTime) / 1000).toFixed(1)
          el.innerText = `${elapsed}s since last ping`
        } else {
          el.innerText = 'Waiting for ping...'
        }
      }
    }

    if (this._lastMinehutUpdateTime) {
      const elapsed = ((now - this._lastMinehutUpdateTime) / 1000).toFixed(1)
      const el = document.getElementById('minehut-timer')
      if (el) {
        el.innerText = `${elapsed}s since last update`
      }
    }
  }

  updateGlobalStats = () => {
    // Only redraw when needed
    // These operations are relatively cheap, but the site already does too much rendering
    const totalPlayerCount = this.getTotalPlayerCount()

    if (totalPlayerCount !== this._lastTotalPlayerCount) {
      this._lastTotalPlayerCount = totalPlayerCount
      document.getElementById('stat_totalPlayers').innerText = formatNumber(totalPlayerCount)
    }

    // Only redraw when needed
    // These operations are relatively cheap, but the site already does too much rendering
    const serverRegistrationCount = this.serverRegistry.getServerRegistrations().length

    if (serverRegistrationCount !== this._lastServerRegistrationCount) {
      this._lastServerRegistrationCount = serverRegistrationCount
      document.getElementById('stat_networks').innerText = serverRegistrationCount
    }

    this.updateNetworkTotals()
  }
}
